'use client';

import { useMemo } from 'react';
import { formatCompactAmount } from '../lib/formatCompactAmount';
import { currencyFractionDigits, formatMoney } from '../lib/money/formatMoney';
import { cn } from '../lib/shadcn';
import { useAnimatedNumber } from '../lib/useAnimatedNumber';
import { FigureText } from './figure-text';

export interface AnimatedAmountProps {
  value: number;
  currency?: string | null;
  locale?: string;
  decimals?: number;
  duration?: number;
  enabled?: boolean;
  className?: string;
  prefix?: string;
  suffix?: string;
  compactNotation?: boolean;
  /** Lets a hero figure wrap after its group separators instead of overflowing. */
  breakAtGroups?: boolean;
}

export function AnimatedAmount({
  value,
  currency = 'CLP',
  locale = 'es-CL',
  decimals,
  duration = 500,
  enabled = true,
  className,
  prefix = '',
  suffix = '',
  compactNotation = false,
  breakAtGroups = false,
}: AnimatedAmountProps) {
  const digits = decimals ?? (currency ? currencyFractionDigits(currency) : 0);
  const animatedValue = useAnimatedNumber(value, duration, enabled, digits);

  const formatted = useMemo(() => {
    if (compactNotation) {
      return formatCompactAmount(animatedValue, locale, currency);
    }

    // es-CL writes a negative amount as "$-1.180.990"; the sign leads here, as it does in the compact form.
    const sign = animatedValue < 0 ? '-' : '';
    if (currency)
      return `${sign}${formatMoney(Math.abs(animatedValue), currency, locale, decimals)}`;
    const amount = new Intl.NumberFormat(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(Math.abs(animatedValue));
    return `${sign}${amount}`;
  }, [animatedValue, currency, locale, decimals, digits, compactNotation]);

  const text = `${prefix}${formatted}${suffix}`;
  return (
    // One text node: separate ones get a comment between them in the server HTML.
    // FigureText puts a <wbr> between its parts, so no two text nodes touch.
    <span className={cn(className)}>{breakAtGroups ? <FigureText>{text}</FigureText> : text}</span>
  );
}

export function AnimatedCLP(props: Omit<AnimatedAmountProps, 'currency'>) {
  return <AnimatedAmount {...props} currency="CLP" />;
}

export function AnimatedPercentage(props: Omit<AnimatedAmountProps, 'currency' | 'suffix'>) {
  return <AnimatedAmount {...props} currency={null} suffix="%" decimals={1} />;
}
